import React from 'react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, currency, useModalControl } from '@frontend/design-system';
import { useCheckCanSendMessages } from '../../hooks/use-check-can-send-messages';
import { useScheduleRequestItemHeaderController } from '../../hooks/use-schedule-request-item-header-controller';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestItemHeaderUI } from '../components/schedule-request-item-header-ui';

interface Props {
  requestVersion: string;
  name: string;
  appointmentTime: string;
  personPhone: string;
  paidAmount: number;
  locationId: string;
  scheduleRequestId: string;
  isWidgetListItem?: boolean;
  locationChip?: (locationName: string) => React.ReactNode;
  updateSelectedScheduleRequest: () => void;
  deleteRequestModalProps: ReturnType<typeof useModalControl>;
  rejectRequestModalProps?: ReturnType<typeof useModalControl>;
  onMessage: (updateStatus: boolean) => void;
  onPhoneCall?: () => void;
}

export const ScheduleRequestListItemHeader = React.memo(
  ({
    requestVersion,
    name,
    paidAmount,
    appointmentTime,
    personPhone,
    locationId,
    scheduleRequestId,
    isWidgetListItem,
    updateSelectedScheduleRequest,
    locationChip,
    deleteRequestModalProps,
    rejectRequestModalProps,
    onMessage,
    onPhoneCall,
  }: Props) => {
    const { t } = useTranslation('schedule-request');
    const canSendMessages = useCheckCanSendMessages(locationId);

    const {
      shouldUseV3,
      locationName,
      appointmentTimeLabel,
      getMenuProps,
      getTriggerProps,
      handleReviewRequestMenuItemClick,
      handleRejectRequestMenuItemClick,
      handleDeleteMenuItemClick,
      handleMessageMenuItemClick,
    } = useScheduleRequestItemHeaderController({
      requestVersion,
      locationId,
      scheduleRequestId,
      appointmentTime,
      isWidgetListItem,
      deleteRequestModalProps,
      rejectRequestModalProps,
      onMessage,
      onPhoneCall,
    });

    const secondaryMeta = (
      <div css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
        {!!paidAmount ? (
          <>
            <Text textAlign='left' size='small' css={{ display: 'inline', color: theme.colors.tangerine50 }}>
              {currency(paidAmount, { decimals: 2 })}
            </Text>
            {` • `}
          </>
        ) : null}
        <Text textAlign='left' size='small'>
          {appointmentTimeLabel}
        </Text>
      </div>
    );

    const menuItems = (
      <>
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemReviewRequestBtn}
          Icon={() => <Icon name='lists-small' />}
          onClick={() => handleReviewRequestMenuItemClick(updateSelectedScheduleRequest)}
        >
          {t('Review Request')}
        </PopoverMenuItem>
        {personPhone && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemMessageBtn}
            Icon={() => <Icon name='message-small' />}
            disabled={!canSendMessages}
            hoverLabel={canSendMessages ? undefined : t('You do not have permission to send text messages')}
            onClick={handleMessageMenuItemClick}
          >
            {t('Message')}
          </PopoverMenuItem>
        )}
        {shouldUseV3 && (
          <PopoverMenuItem
            trackingId={ScheduleRequestsTrackingIds.requestListItemRejectBtn}
            Icon={() => <Icon name='calendar-discarded-small' />}
            onClick={handleRejectRequestMenuItemClick}
          >
            {t('Reject Request')}
          </PopoverMenuItem>
        )}
        <PopoverMenuItem
          trackingId={ScheduleRequestsTrackingIds.requestListItemDeleteBtn}
          Icon={() => <Icon name='trash-small' />}
          onClick={handleDeleteMenuItemClick}
        >
          {t('Delete')}
        </PopoverMenuItem>
      </>
    );

    return (
      <ScheduleRequestItemHeaderUI
        name={name}
        locationName={locationName}
        secondaryMeta={secondaryMeta}
        menuItems={menuItems}
        locationChip={locationChip}
        getTriggerProps={getTriggerProps}
        getMenuProps={getMenuProps}
        isWidgetListItem={isWidgetListItem}
      />
    );
  }
);

ScheduleRequestListItemHeader.displayName = 'ScheduleRequestListItemHeader';
